import React, {useCallback, useEffect, useState} from "react";
import UIUtils from "../../../../utils";
import {useDispatch} from "react-redux";
import {useTranslation} from "react-i18next";
import * as IDB from "idb-keyval";
import {setReduxDialogState} from "../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../core/models/static/routes/redux-store-routes";
import Utils from "../../../../../core/services/utils/utils";
import ToastService from "../../../../../core/services/toast";
import {Box, IconButton, Tooltip} from "@mui/material";
import {ImportantDevicesRounded, InstallDesktopRounded} from "@mui/icons-material";


export const InstallPWAButton: React.FC = () => {
    const [installable, setInstallable] = useState(UIUtils.PWAInstallable);
    const [installed, setInstalled] = useState(false);
    const [isPWA, setIsPWA] = useState(UIUtils.PWARunning);
    const dispatch = useDispatch();
    const {t} = useTranslation("components", {keyPrefix: 'panel-sections.drawers.settings-drawer.install-pwa'});

    /**
     * Checks if the PWA is installed.
     */
    const checkIfPWAIsInstalled = useCallback(async () => {
        try {
            const installedApps = (await navigator.getInstalledRelatedApps?.()) ?? [];
            let installed = installedApps.some(app => app.platform === 'webapp');
            if (!installed) {
                installed = await IDB.get<boolean>('pwa-installed') ?? false;
            }
            setInstalled(installed);
        } catch (e) {
            setInstalled(false);
        }
    }, []);


    /**
     * As soon as the component is mounted:
     * - Checks if the application is installable as a PWA.
     * - Checks if the PWA is installed.
     */
    useEffect(() => {
        void checkIfPWAIsInstalled();
        const offPWaInstallable = UIUtils.onPWAInstallable((installable) => {
            void checkIfPWAIsInstalled();
            setInstallable(installable)
        })
        const offPWaRunning = UIUtils.onPWARunning((isPWA) => {
            void checkIfPWAIsInstalled();
            setIsPWA(isPWA)
        })
        return () => {
            offPWaInstallable();
            offPWaRunning();
        }
    }, [checkIfPWAIsInstalled]);

    /**
     * Prompts the user to install the application as a PWA.
     */
    const installApp = useCallback(() => {
        void UIUtils.promptPWAInstallation();
    }, [])

    /**
     * Shows the installation guide to the user.
     */
    const showInstallationGuide = useCallback(() => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.viewInstallationGuide, {open: true}))
    }, [dispatch]);

    /**
     * Opens the installed application.
     */
    const openInstalledApp = useCallback(() => {
        if (Utils.getOsSpecs().isAndroid) {
            window.open(window.location.href, '_blank')
        } else {
            ToastService.warning({
                title: t(t => t.alreadyInstalled.title),
                message: t(t => t.alreadyInstalled.message),
                toastId: 'app-installed'
            } as any)
        }
    }, [t]);

    return (
        <>
            {
                !isPWA && installed &&
                <Tooltip title={t(t => t.installed)}>
                    <IconButton
                        onClick={openInstalledApp}
                        color={'default'}
                        sx={{width: 34, height: 34, minWidth: 34,}}
                    >
                        <Box component={ImportantDevicesRounded} sx={{width: 20, height: 20}}/>
                    </IconButton>
                </Tooltip>
            }
            {
                !isPWA && !installed &&
                <Tooltip title={installable ? t(t => t.install) : t(t => t.howToInstall)}>
                    <IconButton
                        onClick={installable ? installApp : showInstallationGuide}
                        color={'default'}
                        sx={{width: 34, height: 34, minWidth: 34,}}
                    >
                        <Box component={InstallDesktopRounded} sx={{width: 20, height: 20}}/>
                    </IconButton>
                </Tooltip>
            }
        </>
    );
}